/* eslint-disable @typescript-eslint/no-explicit-any */
import { GraphCard } from './GraphCard';
import {
  Box,
  ButtonGroup,
  Card,
  Heading,
  Icon,
  Label,
  Paragraph,
  Popover,
  Text,
  ToggleButton,
} from '@zillow/constellation-10';
import { BuildingType, DataType } from '../containers/Overview';
import { LabeledControl } from '@zillow/constellation-10';
import { Radio } from '@zillow/constellation-10';
import { IconInfoFilled } from '@zillow/constellation-icons';
import type { ComponentProps, Dispatch, SetStateAction } from 'react';
import { isNumber } from '../../utils/isNumber';
import { formatNumber } from '../../utils/formatNumber';
import { Tag } from '@zillow/constellation-10';

type TagTone = ComponentProps<typeof Tag>['tone'];

export interface GraphWrapperInfoCard {
  title: string;
  infoText: string;
  value: number | string;
  formatter?: (value: number | string) => string;
  tag?: string | React.ReactNode;
  tagTone?: TagTone;
}

export interface GraphWrapperProps {
  title: string;
  subtitle: string;
  graphId?: string;
  buttonGroupEnum?: { [key: string]: string };
  buttonGroupFilter?: unknown;
  setButtonGroupFilter?: Dispatch<SetStateAction<any>>;
  radioButtonEnum?: { [key: string]: string };
  radioButtonEnumLabelMapping?: Record<string, string>;
  radioButtonFilter?: unknown;
  setRadioButtonFilter?: Dispatch<SetStateAction<any>>;
  children: React.ReactNode;
  loading?: boolean;
  error?: any;
  bottomText?: string;
  infoCards: Array<GraphWrapperInfoCard>;
  toggleControl?: React.ReactNode;
  customFilters?: React.ReactNode;
  headerAction?: React.ReactNode;
}

const GraphDataInnerCard = ({
  title,
  infoText,
  value,
  formatter,
  tag,
  tagTone = 'gray',
}: {
  title: string;
  infoText: string;
  value: number | string;
  formatter?: (value: number | string) => string;
  tag?: string | React.ReactNode;
  tagTone?: TagTone;
}) => {
  const formattedValue = formatter ? formatter(value) : isNumber(value) ? formatNumber(value) : value;
  return (
    <Card css={{ padding: '400' }} elevated={false}>
      <Popover
        header={<Text textStyle="body-sm-bold">{title}</Text>}
        closeButton={<span hidden />}
        body={<Paragraph textStyle="body-sm">{infoText}</Paragraph>}
        trigger={
          <Box
            css={{
              display: 'flex',
              alignItems: 'center',
              flexWrap: 'nowrap',
              gap: '100',
              cursor: 'pointer',
            }}
          >
            <Text textStyle="body-sm-bold" asChild css={{ whiteSpace: 'nowrap' }}>
              <h4>{title}</h4>
            </Text>
            <Icon size="lg" css={{ color: 'gray.500', paddingRight: '13px' }}>
              <IconInfoFilled />
            </Icon>
          </Box>
        }
        shouldAwaitInteractionResponse
        tone="neutral"
      />
      <Heading level={5} css={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '100' }}>
        {formattedValue}
        {tag && (
          <Tag tone={tagTone} css={{ whiteSpace: 'nowrap', '& > svg': { display: 'none' } }}>
            {tag}
          </Tag>
        )}
      </Heading>
    </Card>
  );
};

export const GraphWrapper = ({
  title,
  subtitle,
  graphId,
  buttonGroupEnum = BuildingType,
  buttonGroupFilter,
  setButtonGroupFilter,
  radioButtonEnum = DataType,
  radioButtonEnumLabelMapping,
  radioButtonFilter,
  setRadioButtonFilter,
  children,
  infoCards,
  bottomText,
  loading = false,
  error,
  toggleControl,
  customFilters,
  headerAction,
}: GraphWrapperProps) => {
  return (
    <GraphCard loading={loading} error={error} data-graph-id={graphId}>
      <Box
        css={{
          display: 'flex',
          justifyContent: 'space-between',
          flexDirection: { base: 'column', xl: 'row' },
          gap: '600',
          alignItems: { base: 'flex-start', xl: 'flex-start' },
          marginBottom: '10px',
        }}
      >
        <Box>
          <Heading level={5}>{title}</Heading>
          <Paragraph>{subtitle}</Paragraph>
          {headerAction}
        </Box>
        <Box css={{ display: 'flex', gap: 'default' }}>
          {infoCards.map((infoCard) => (
            <GraphDataInnerCard
              key={infoCard.title}
              title={infoCard.title}
              infoText={infoCard.infoText}
              value={infoCard.value}
              formatter={infoCard.formatter}
              tag={infoCard.tag}
              tagTone={infoCard.tagTone}
            />
          ))}
        </Box>
      </Box>
      <Box
        css={{
          display: 'flex',
          flexWrap: 'wrap',
          flexDirection: { base: 'column', xxl: 'row' },
          justifyContent: { base: 'flex-start', xxl: 'space-between' },
          alignItems: { base: 'flex-start', xxl: 'center' },
          marginBottom: '800',
          gap: '400',
        }}
      >
        <Box
          css={{
            display: 'flex',
            flexDirection: { base: 'column', lg: 'row' },
            alignItems: { base: 'flex-start', lg: 'center' },
            gap: '800',
            marginInlineEnd: '400',
          }}
        >
          {buttonGroupFilter && setButtonGroupFilter ? (
            <ButtonGroup aria-label="building type filter" conjoined orientation="horizontal" role="group">
              {Object.values(buttonGroupEnum).map((value: string) => (
                <ToggleButton
                  key={value}
                  value={value}
                  selected={buttonGroupFilter === value}
                  onClick={() => setButtonGroupFilter(value)}
                  css={{ whiteSpace: 'nowrap' }}
                >
                  {value}
                </ToggleButton>
              ))}
            </ButtonGroup>
          ) : null}
          {radioButtonFilter && setRadioButtonFilter ? (
            <Box
              css={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: 'default',
              }}
            >
              {Object.values(radioButtonEnum).map((value: string) => (
                <LabeledControl
                  key={value}
                  control={
                    <Radio
                      checked={radioButtonFilter === value}
                      name={title.split(' ').join('-')}
                      onChange={(e) => e.target.checked && setRadioButtonFilter(value)}
                      value={value}
                    />
                  }
                  label={<Label>{radioButtonEnumLabelMapping?.[value] ?? value}</Label>}
                />
              ))}
            </Box>
          ) : null}
          {customFilters}
        </Box>
        {toggleControl}
      </Box>
      <Box css={{ '& svg': { overflow: 'visible !important' } }}>{children}</Box>
      {bottomText && (
        <Paragraph
          css={{ textAlign: 'center', color: 'text.subtle', marginTop: '600', position: 'relative', top: '600' }}
        >
          {bottomText}
        </Paragraph>
      )}
    </GraphCard>
  );
};
